import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Body5, Container, SVG_LOADING_ICON_TYPES, SvgLoadingIcon } from '@ui';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { ROUTES } from '@constants/routes';
import { useColorTheme } from '@hooks';
import { fetchUserDocs } from '@hooks/features/useDocsCache';
import { convertHexToRGBA, truncateStringWithDate } from '@utils/usefulFuncs';

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 480px;
  overflow-y: auto;
`;
const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 4px;
  background: ${({ theme }) => convertHexToRGBA(theme.themeColors.lowContrastBorder, 1)};
  cursor: pointer;
  transition: all 0.14s;

  &:hover {
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.05)};
    border: 1px solid ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.2)};
  }

  &.selected {
    border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    background: ${({ theme }) => convertHexToRGBA(theme.themeColors.primaryCtaButton, 0.2)};
  }
`;
const Filename = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;
const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 16px;
  text-align: center;
`;

const TRUNCATE_LENGTH = 80;

const isCsv = (doc) => {
  const name = (doc && doc.filename) || '';
  return name.toLowerCase().endsWith('.csv');
};

export const CsvUserDocumentSelection = ({ selectedDocs, onSelect, onRemoveSelect }) => {
  const navigate = useNavigate();
  const colorTheme = useColorTheme();
  const [isLoading, setIsLoading] = React.useState(true);
  const [docs, setDocs] = React.useState([]);

  React.useEffect(() => {
    const ref = { isCanceled: false };
    const run = async () => {
      const result = await fetchUserDocs();
      if (ref.isCanceled) return;
      setDocs(Array.isArray(result) ? result.filter(isCsv) : []);
      setIsLoading(false);
    };
    run();
    return () => {
      ref.isCanceled = true;
    };
  }, []);

  const selectedIds = React.useMemo(() => new Set((selectedDocs || []).map((d) => d.id)), [selectedDocs]);

  if (isLoading) {
    return (
      <Container row verticallyCenter top={48} bottom={48}>
        <SvgLoadingIcon type={SVG_LOADING_ICON_TYPES.bars} color={colorTheme.text} />
        <Container left={16}>
          <Body5>Loading CSV files...</Body5>
        </Container>
      </Container>
    );
  }

  if (!docs.length) {
    return (
      <EmptyWrapper>
        <Body5 isLowContrast>No CSV files in your library.</Body5>
        <SkeletonButton onClick={() => navigate(ROUTES.docManagement)}>Upload CSV</SkeletonButton>
      </EmptyWrapper>
    );
  }

  return (
    <ListWrapper>
      {docs.map((doc) => {
        const isSelected = selectedIds.has(doc.id);
        const handleClick = () => {
          if (isSelected) {
            if (onRemoveSelect) onRemoveSelect(doc);
            return;
          }
          onSelect(doc);
        };
        return (
          <Row key={doc.id} className={isSelected ? 'selected' : ''} onClick={handleClick}>
            <Filename>
              <Body5>{truncateStringWithDate(doc.filename, TRUNCATE_LENGTH)}</Body5>
            </Filename>
            {isSelected ? (
              <Container left={16}>
                <Body5 isLowContrast>Selected</Body5>
              </Container>
            ) : null}
          </Row>
        );
      })}
    </ListWrapper>
  );
};
